import { useRef, useState, useEffect, useCallback } from "react";

export default function VoteCard({ menus, currentIndex, onSwipe, onUndo, canUndo, restaurantInfo, searchProvider = 'naver' }) {
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const cardRef = useRef(null);
  const currentMenu = menus[currentIndex];
  const total = menus.length;
  const info = restaurantInfo?.[currentIndex];
  const SWIPE_THRESHOLD = 80;

  const animateOut = useCallback((direction) => {
    const target = direction === "right" ? 400 : -400;
    setDragX(target);
    setTimeout(() => {
      setDragX(0);
      setSwipeDirection(null);
      onSwipe(direction === "right" ? "ok" : "pass");
    }, 200);
  }, [onSwipe]);

  useEffect(() => {
    const handler = (e) => {
      if (!currentMenu) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); animateOut("right"); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); animateOut("left"); }
      else if (e.key === 'z' && (e.ctrlKey || e.metaKey) && canUndo) { e.preventDefault(); onUndo(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [currentMenu, canUndo, onUndo, animateOut]);

  const handleStart = (x) => { startXRef.current = x; setIsDragging(true); };
  const handleMove = (x) => {
    if (!isDragging) return;
    const diff = x - startXRef.current;
    setDragX(diff);
    setSwipeDirection(diff > SWIPE_THRESHOLD ? "right" : diff < -SWIPE_THRESHOLD ? "left" : null);
  };
  const handleEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragX > SWIPE_THRESHOLD) animateOut("right");
    else if (dragX < -SWIPE_THRESHOLD) animateOut("left");
    else { setDragX(0); setSwipeDirection(null); }
  };

  const getMapUrl = (name) => searchProvider === 'kakao'
    ? `https://map.kakao.com/?q=${encodeURIComponent(name)}`
    : `https://map.naver.com/v5/search/${encodeURIComponent(name)}`;

  const rotation = dragX * 0.08;
  const opacity = Math.max(0, 1 - Math.abs(dragX) / 500);
  const wm = { position:"absolute",top:"24px",fontSize:"32px",fontWeight:"900",letterSpacing:"4px",opacity:0.85,pointerEvents:"none",borderWidth:"4px",borderStyle:"solid",borderRadius:"8px",padding:"4px 14px",transform:"rotate(-15deg)",userSelect:"none" };
  const borderColor = swipeDirection === "right" ? "#22c55e" : swipeDirection === "left" ? "#ef4444" : "rgba(255,255,255,0.15)";

  return (
    <div style={S.wrapper}>
      <div style={S.progressContainer}>
        <div style={S.progressLabel}>{currentIndex + 1} / {total}</div>
        <div style={S.progressBarBg}><div style={{ ...S.progressBarFill, width: `${((currentIndex+1)/total)*100}%` }} /></div>
      </div>
      <div style={S.cardArea}>
        {currentMenu ? (
          <div ref={cardRef} style={{ ...S.card, borderColor, borderWidth:"3px", borderStyle:"solid", transform:`translateX(${dragX}px) rotate(${rotation}deg)`, opacity, transition: isDragging ? "none" : "transform 0.2s ease, opacity 0.2s ease", cursor: isDragging ? "grabbing" : "grab" }}
            onTouchStart={(e)=>handleStart(e.touches[0].clientX)} onTouchMove={(e)=>handleMove(e.touches[0].clientX)} onTouchEnd={handleEnd}
            onMouseDown={(e)=>{e.preventDefault();handleStart(e.clientX)}} onMouseMove={(e)=>{if(isDragging)handleMove(e.clientX)}} onMouseUp={handleEnd} onMouseLeave={()=>{if(isDragging)handleEnd()}}
            role="button" tabIndex={0} aria-label={`${currentMenu} - 오른쪽 OK, 왼쪽 PASS`}>
            {swipeDirection==="left" && <span style={{...wm,color:"#ef4444",borderColor:"#ef4444",left:"24px"}}>PASS</span>}
            {swipeDirection==="right" && <span style={{...wm,color:"#22c55e",borderColor:"#22c55e",right:"24px"}}>OK</span>}
            <span style={S.menuName}>{currentMenu}</span>
            {info && <div style={S.infoRow}>
              {info.category && <span style={S.catChip}>{info.category}</span>}
              {info.distance > 0 && <span style={S.dist}>📍 {info.distance>=1000?`${(info.distance/1000).toFixed(1)}km`:`${info.distance}m`}</span>}
            </div>}
            {info?.address && <p style={S.addr}>{info.address}</p>}
            <a href={getMapUrl(currentMenu)} target="_blank" rel="noopener noreferrer" style={S.mapLink}
              onClick={e=>e.stopPropagation()} onMouseDown={e=>e.stopPropagation()} onTouchStart={e=>e.stopPropagation()}>🗺️ 지도에서 보기</a>
          </div>
        ) : (
          <div style={S.doneCard}><span style={S.doneText}>모든 메뉴를 확인했어요!</span></div>
        )}
      </div>
      <p style={S.hint}>카드를 밀거나 버튼을 눌러 투표하세요<br/><span style={{fontSize:"11px",opacity:0.6}}>⌨️ ← PASS · → OK · Ctrl+Z 되돌리기</span></p>
      <div style={S.btnRow}>
        <button style={S.passBtn} onClick={()=>animateOut("left")} disabled={!currentMenu} aria-label="PASS">👎 PASS</button>
        <button style={S.okBtn} onClick={()=>animateOut("right")} disabled={!currentMenu} aria-label="OK">👍 OK</button>
      </div>
      {canUndo && <button style={S.undoBtn} onClick={onUndo} aria-label="되돌리기">↩️ 이전 메뉴로 되돌리기</button>}
    </div>
  );
}

const S = {
  wrapper:{display:"flex",flexDirection:"column",alignItems:"center",padding:"16px",maxWidth:"480px",margin:"0 auto",fontFamily:"var(--font-family)"},
  progressContainer:{width:"100%",marginBottom:"20px"},
  progressLabel:{textAlign:"center",fontSize:"14px",fontWeight:"600",color:"var(--text-secondary)",marginBottom:"6px"},
  progressBarBg:{width:"100%",height:"8px",backgroundColor:"rgba(255,255,255,0.1)",borderRadius:"var(--radius-full)",overflow:"hidden"},
  progressBarFill:{height:"100%",background:"var(--accent-gradient)",borderRadius:"var(--radius-full)",transition:"width 0.4s ease"},
  cardArea:{width:"85%",position:"relative",display:"flex",justifyContent:"center"},
  card:{width:"100%",minHeight:"340px",background:"linear-gradient(145deg, rgba(30,41,59,0.95) 0%, rgba(15,23,42,0.95) 100%)",borderRadius:"var(--radius-xl)",boxShadow:"var(--shadow-card)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",position:"relative",overflow:"hidden",userSelect:"none",padding:"24px 16px"},
  menuName:{fontSize:"clamp(24px,6vw,40px)",fontWeight:"800",color:"var(--text-primary)",textAlign:"center",padding:"0 20px",pointerEvents:"none"},
  infoRow:{display:"flex",alignItems:"center",gap:"8px",marginTop:"12px",pointerEvents:"none"},
  catChip:{fontSize:"12px",fontWeight:"600",color:"var(--accent-amber)",background:"rgba(245,158,11,0.15)",border:"1px solid rgba(245,158,11,0.3)",borderRadius:"var(--radius-full)",padding:"3px 10px"},
  dist:{fontSize:"12px",color:"var(--text-muted)"},
  addr:{fontSize:"12px",color:"var(--text-muted)",textAlign:"center",marginTop:"8px",padding:"0 16px",lineHeight:1.4,pointerEvents:"none",margin:"8px 0 0"},
  mapLink:{marginTop:"10px",fontSize:"12px",fontWeight:"600",color:"#4ade80",background:"rgba(34,197,94,0.1)",border:"1px solid rgba(34,197,94,0.25)",borderRadius:"var(--radius-full)",padding:"4px 12px",textDecoration:"none",cursor:"pointer"},
  doneCard:{width:"100%",height:"340px",background:"rgba(255,255,255,0.05)",borderRadius:"var(--radius-xl)",boxShadow:"0 4px 16px rgba(0,0,0,0.2)",display:"flex",alignItems:"center",justifyContent:"center"},
  doneText:{fontSize:"18px",fontWeight:"600",color:"var(--text-muted)"},
  hint:{marginTop:"14px",fontSize:"13px",color:"var(--text-muted)",textAlign:"center",lineHeight:1.6},
  btnRow:{display:"flex",gap:"24px",marginTop:"8px"},
  passBtn:{padding:"14px 32px",fontSize:"18px",fontWeight:"700",fontFamily:"var(--font-family)",backgroundColor:"rgba(239,68,68,0.15)",color:"#ef4444",border:"1.5px solid rgba(239,68,68,0.3)",borderRadius:"50px",cursor:"pointer",transition:"transform 0.1s",boxShadow:"0 2px 8px rgba(239,68,68,0.15)"},
  okBtn:{padding:"14px 32px",fontSize:"18px",fontWeight:"700",fontFamily:"var(--font-family)",backgroundColor:"rgba(34,197,94,0.15)",color:"#22c55e",border:"1.5px solid rgba(34,197,94,0.3)",borderRadius:"50px",cursor:"pointer",transition:"transform 0.1s",boxShadow:"0 2px 8px rgba(34,197,94,0.15)"},
  undoBtn:{marginTop:"12px",padding:"8px 20px",fontSize:"14px",fontWeight:"600",fontFamily:"var(--font-family)",background:"rgba(255,255,255,0.07)",color:"var(--text-secondary)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:"var(--radius-full)",cursor:"pointer"},
};
